import React, { useState, useEffect } from 'react';
import { ExportModal } from './ExportModal';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { useToast } from '../contexts/ToastContext';

interface SettingsData {
  showPricing?: boolean;
  showPerformance?: boolean;
}

interface Vehicle {
  id: number;
  year: number;
  make: string;
  model: string;
  trim: string;
  color?: string;
  nickname?: string;
  description?: string;
  display_name: string;
  horsepower?: number;
  torque?: number;
  redline?: number;
}

interface Modification {
  id: string;
  title: string;
  description: string;
  category: string;
  vendor: string;
  cost?: number;
  install_date?: string;
  notes?: string;
  part_number?: string;
  specifications?: any;
  parentComponent?: string;
  power_gain?: number;
  torque_gain?: number;
  weight_change?: number;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: SettingsData;
  onSave: (settings: SettingsData) => void | Promise<void>;
  onImportClick?: () => void;
  onDeleteVehicle?: () => void;
  isOwner?: boolean;
  vehicleName?: string;
  vehicle?: Vehicle;
  modifications?: Modification[];
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSave,
  onImportClick,
  onDeleteVehicle,
  isOwner = false,
  vehicleName = 'this vehicle',
  vehicle,
  modifications = []
}) => {
  const { showToast } = useToast();
  const [localSettings, setLocalSettings] = useState<SettingsData>(settings);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<'json' | 'excel' | 'pdf' | 'text'>('json');

  useEffect(() => {
    if (isOpen) {
      setLocalSettings(settings);
    }
  }, [isOpen, settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await Promise.resolve(onSave(localSettings));
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleModalClose = () => {
    if (showDeleteConfirm) {
      setShowDeleteConfirm(false);
      return;
    }
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteVehicle?.();
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleExportClick = (type: 'json' | 'excel' | 'pdf' | 'text') => {
    if (!vehicle) {
      showToast('Vehicle data not available for export', 'error');
      return;
    }
    setExportType(type);
    setIsExportModalOpen(true);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        title={showDeleteConfirm ? 'Confirm vehicle deletion' : 'Settings'}
        description={showDeleteConfirm ? 'This action cannot be undone.' : undefined}
        size="lg"
        className="max-h-[90vh] overflow-y-auto"
      >
        {!showDeleteConfirm ? (
          <>
            <div className="space-y-8">
              {/* Display Options - Only show if settings include display properties */}
              {(settings.showPricing !== undefined || settings.showPerformance !== undefined) && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Display Options</h3>
                  <div className="space-y-6">
                    {/* Show Pricing Toggle */}
                    {settings.showPricing !== undefined && (
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-white font-medium">Show Pricing</label>
                          <p className="text-sm text-gray-400">Display total investment and individual modification costs</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!localSettings.showPricing}
                          aria-label="Show pricing"
                          onClick={() => setLocalSettings(prev => ({ ...prev, showPricing: !prev.showPricing }))}
                          className={`toggle-track ${localSettings.showPricing ? 'toggle-track-on' : ''}`}
                        >
                          <span
                            className={`toggle-thumb ${localSettings.showPricing ? 'toggle-thumb-on' : ''}`}
                          />
                        </button>
                      </div>
                    )}

                    {/* Show Performance Toggle */}
                    {settings.showPerformance !== undefined && (
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-white font-medium">Show Performance Stats</label>
                          <p className="text-sm text-gray-400">Display horsepower, torque, and redline information</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!localSettings.showPerformance}
                          aria-label="Show performance stats"
                          onClick={() => setLocalSettings(prev => ({ ...prev, showPerformance: !prev.showPerformance }))}
                          className={`toggle-track ${localSettings.showPerformance ? 'toggle-track-on' : ''}`}
                        >
                          <span
                            className={`toggle-thumb ${localSettings.showPerformance ? 'toggle-thumb-on' : ''}`}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Data Management - Only for owners */}
              {isOwner && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4">Export & delete</h3>
                  <div className="space-y-4">
                    {/* Export Vehicle Data */}
                    <div className="surface rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-medium mb-1">Export this build</h4>
                          <p className="text-sm text-gray-400 mb-3">
                            Download this build’s data for backup, sharing, or documentation.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                        <button
                          onClick={() => handleExportClick('json')}
                          className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-4-4m4 4l4-4m-4-4V3" />
                          </svg>
                          <span>JSON</span>
                        </button>
                        <button
                          onClick={() => handleExportClick('excel')}
                          className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Excel</span>
                        </button>
                        <button
                          onClick={() => handleExportClick('pdf')}
                          className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span>PDF</span>
                        </button>
                        <button
                          onClick={() => handleExportClick('text')}
                          className="flex items-center justify-center space-x-2 btn-primary btn-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span>Text</span>
                        </button>
                      </div>
                    </div>

                    {/* Import build list */}
                    <div className="rounded-lg border border-white/[0.07] bg-[#0c0c0c]/50 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-medium mb-1 flex items-center space-x-2">
                            <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Import build list</span>
                          </h4>
                          <p className="text-sm text-gray-300 mb-3">
                            Paste the list you keep in Notes, or upload a spreadsheet or document. Bilt organizes parts for you to review.
                          </p>
                          <div className="text-xs text-gray-500">
                            Excel, CSV, PDF, or text · max 10 MB
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          onImportClick?.();
                          onClose();
                        }}
                        className="mt-3 w-full btn-primary"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Import build list</span>
                      </button>
                    </div>

                    {/* Delete Vehicle Section */}
                    {onDeleteVehicle && (
                      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-red-400 font-medium mb-1">Delete Vehicle</h4>
                            <p className="text-sm text-gray-400 mb-3">
                              Permanently delete this vehicle and all associated data including modifications, images, and performance records.
                            </p>
                            <div className="text-xs text-red-400">
                              ⚠️ This action cannot be undone
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={handleDeleteClick}
                          className="mt-3 w-full flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Delete Vehicle</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {(settings.showPricing !== undefined || settings.showPerformance !== undefined) && (
              <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-white/[0.08]">
                <button type="button" onClick={onClose} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="btn-primary disabled:opacity-50"
                >
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-900/40 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            <p className="text-gray-300 mb-4">
              Are you sure you want to delete <strong className="text-white">&quot;{vehicleName}&quot;</strong>?
            </p>
            <div className="bg-red-900/20 border border-red-700 rounded-lg p-3 mb-6">
              <p className="text-sm text-red-400 font-medium mb-2">This will permanently delete:</p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>All modifications and specifications</li>
                <li>All vehicle images and photos</li>
                <li>Performance data and records</li>
                <li>Vehicle settings and preferences</li>
                <li>All associated activity logs</li>
              </ul>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t border-white/[0.08]">
              <Button type="button" variant="ghost" onClick={handleDeleteCancel} className="min-h-[44px]">
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={handleDeleteConfirm} className="min-h-[44px]">
                Delete vehicle
              </Button>
            </div>
          </>
        )}
      </Modal>

      {isExportModalOpen && vehicle && (
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          vehicle={vehicle}
          modifications={modifications}
          exportType={exportType}
        />
      )}
    </>
  );
}; 